/**
 * One named rule in a dialog (ADR-0100): its name, its type, the words or the entities it watches,
 * and its severity. *Add rule* opens it empty, the pencil on a rule opens it with that rule; a
 * saved rule keeps its type. *Mentioned in a new source* is not made here: it comes with *Watch*.
 */
import { type FormEvent, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type EntityOption,
  EntityPicker,
  type EntitySearch,
} from '@/components/entity-picker/EntityPicker'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { RuleSeverity } from '@/services/api/case-monitor'
import { RULE_SEVERITIES } from './SeverityBadge'

const FORM = 'cases.monitor.rules.form'

/** The types a rule is made as here; *occurrence* comes with watching an entity. */
export const EDITABLE_RULE_KINDS = ['phrase', 'new_connection', 'state_change'] as const
export type EditableRuleKind = (typeof EDITABLE_RULE_KINDS)[number]

export function isEditableKind(kind: string | null): kind is EditableRuleKind {
  return (EDITABLE_RULE_KINDS as readonly (string | null)[]).includes(kind)
}

/** A rule as the form hands it over; `phrasesText` is what was typed (the feature splits it). */
export interface RuleDraft {
  name: string
  kind: EditableRuleKind
  severity: RuleSeverity
  phrasesText: string
  /** A new-connection or state-change rule over every watched entity, in place of `entities`. */
  allWatched: boolean
  entities: readonly EntityOption[]
}

export const EMPTY_DRAFT: RuleDraft = {
  name: '',
  kind: 'phrase',
  severity: 'medium',
  phrasesText: '',
  allWatched: true,
  entities: [],
}

/** What the dialog works on: a new rule (`ruleId` null) or a saved one, and the draft it starts from. */
export interface RuleTarget {
  ruleId: string | null
  draft: RuleDraft
}

/** The feature's way of changing the rules, for a reader who may. */
export interface RuleEditing {
  /** Makes a rule (`ruleId` null) or saves one; rejects when it was not stored (the feature says why). */
  onSubmit: (draft: RuleDraft, ruleId: string | null) => Promise<void>
  onDelete: (ruleId: string) => void
  pending: boolean
  entitySearch: EntitySearch
}

export interface RuleFormProps {
  target: RuleTarget
  edit: Pick<RuleEditing, 'entitySearch' | 'pending'>
  onSubmit: (draft: RuleDraft) => void
  onCancel: () => void
}

/** Whether the draft can be saved: a name, and words for a phrase rule, else which entities. */
export function isReady(draft: RuleDraft): boolean {
  if (draft.name.trim() === '') return false
  if (draft.kind === 'phrase') return draft.phrasesText.trim() !== ''
  return draft.allWatched || draft.entities.length > 0
}

function Choice<V extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  label: { id: string; text: string }
  value: V
  options: readonly { value: V; text: string }[]
  onChange: (value: V) => void
  disabled?: boolean
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label id={label.id}>{label.text}</Label>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={next => onChange(options.find(o => o.value === next)?.value ?? value)}
      >
        <SelectTrigger aria-labelledby={label.id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function WordsField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{t(`${FORM}.words`)}</Label>
      <Textarea
        id={id}
        rows={3}
        value={value}
        placeholder={t(`${FORM}.wordsPlaceholder`)}
        aria-describedby={`${id}-hint`}
        onChange={event => onChange(event.target.value)}
      />
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {t(`${FORM}.wordsHint`)}
      </p>
    </div>
  )
}

const SCOPES = ['all', 'chosen'] as const

function EntitiesField({
  search,
  draft,
  onChange,
}: {
  search: EntitySearch
  draft: RuleDraft
  onChange: (fields: Partial<RuleDraft>) => void
}) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label id={id}>{t(`${FORM}.scope`)}</Label>
        <RadioGroup
          aria-labelledby={id}
          value={draft.allWatched ? 'all' : 'chosen'}
          onValueChange={value => onChange({ allWatched: value === 'all' })}
        >
          {SCOPES.map(scope => (
            <Label key={scope} className="flex items-center gap-2 font-normal">
              <RadioGroupItem value={scope} />
              {t(`${FORM}.scopes.${scope}`)}
            </Label>
          ))}
        </RadioGroup>
      </div>
      {draft.allWatched ? null : (
        <EntityPicker
          search={search}
          selected={draft.entities}
          onChange={entities => onChange({ entities })}
          copy={{
            label: t(`${FORM}.entities`),
            placeholder: t(`${FORM}.entitiesPlaceholder`),
            hint: t(`${FORM}.entitiesHint`),
            searching: t(`${FORM}.entitiesSearching`),
            failed: t(`${FORM}.entitiesFailed`),
            noResults: t(`${FORM}.entitiesNoResults`),
            remove: name => t(`${FORM}.removeEntity`, { name }),
          }}
        />
      )}
    </div>
  )
}

function NameField({ value, onChange }: { value: string; onChange: (name: string) => void }) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{t(`${FORM}.name`)}</Label>
      <Input
        id={id}
        value={value}
        maxLength={255}
        placeholder={t(`${FORM}.namePlaceholder`)}
        onChange={event => onChange(event.target.value)}
      />
    </div>
  )
}

/** The type, fixed once the rule is saved; under it what the type does, or why it cannot change. */
function KindField({
  value,
  fixed,
  onChange,
}: {
  value: EditableRuleKind
  fixed: boolean
  onChange: (kind: EditableRuleKind) => void
}) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <Choice<EditableRuleKind>
        label={{ id, text: t(`${FORM}.kind`) }}
        value={value}
        disabled={fixed}
        options={EDITABLE_RULE_KINDS.map(k => ({
          value: k,
          text: t(`cases.monitor.rules.kinds.${k}`),
        }))}
        onChange={onChange}
      />
      <p className="text-xs text-muted-foreground">
        {fixed ? t(`${FORM}.kindFixed`) : t(`${FORM}.hints.${value}`)}
      </p>
    </div>
  )
}

/** The fields and the two buttons; the dialog around them is `RuleDialog`'s. */
export function RuleForm({ target, edit, onSubmit, onCancel }: RuleFormProps) {
  const { t } = useTranslation()
  const id = useId()
  const [draft, setDraft] = useState<RuleDraft>(target.draft)
  const change = (fields: Partial<RuleDraft>) => setDraft(current => ({ ...current, ...fields }))
  const ready = !edit.pending && isReady(draft)
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (ready) onSubmit(draft)
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <NameField value={draft.name} onChange={name => change({ name })} />
      <KindField
        value={draft.kind}
        fixed={target.ruleId !== null}
        onChange={kind => change({ kind })}
      />
      {draft.kind === 'phrase' ? (
        <WordsField value={draft.phrasesText} onChange={phrasesText => change({ phrasesText })} />
      ) : (
        <EntitiesField search={edit.entitySearch} draft={draft} onChange={change} />
      )}
      <Choice<RuleSeverity>
        label={{ id: `${id}-severity`, text: t(`${FORM}.severity`) }}
        value={draft.severity}
        options={RULE_SEVERITIES.map(s => ({ value: s, text: t(`cases.monitor.severity.${s}`) }))}
        onChange={severity => change({ severity })}
      />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t(`${FORM}.cancel`)}
        </Button>
        <Button type="submit" disabled={!ready}>
          {t(`${FORM}.save`)}
        </Button>
      </DialogFooter>
    </form>
  )
}

export interface RuleDialogProps {
  /** The rule the dialog shows; it stays while the dialog closes, so the form does not blink. */
  target: RuleTarget
  open: boolean
  onOpenChange: (open: boolean) => void
  edit: RuleEditing
}

/** A new rule or a saved one in the app's dialog; it closes once the rule is stored. */
export function RuleDialog({ target, open, onOpenChange, edit }: RuleDialogProps) {
  const { t } = useTranslation()
  const submit = async (draft: RuleDraft) => {
    try {
      await edit.onSubmit(draft, target.ruleId)
      onOpenChange(false)
    } catch {
      // The feature has said what failed; the dialog stays open with what was typed.
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {target.ruleId
              ? t(`${FORM}.editTitle`, { name: target.draft.name })
              : t(`${FORM}.newTitle`)}
          </DialogTitle>
          <DialogDescription>{t(`${FORM}.description`)}</DialogDescription>
        </DialogHeader>
        <RuleForm
          target={target}
          edit={edit}
          onSubmit={draft => void submit(draft)}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}
